import type { CluColours } from '@/components/ui/clu-colours';

/**
 * The agents' colours for Clu's bird, by the key the server stores on each
 * agent (`markColor`), in the order new agents take them. Blue is left out:
 * it is Clu's own. Keys, not places, so the palette can grow or be reordered
 * without an agent's colour changing.
 */
export const MARK_COLOURS_BY_KEY = {
  violet: { disc: '#8458EC', beak: '#6A3FD6', eye: '#2E1670' },
  orange: { disc: '#EE7B34', beak: '#D06120', eye: '#5E2608' },
  green: { disc: '#26A568', beak: '#1A8752', eye: '#07361F' },
  pink: { disc: '#E0529C', beak: '#C23A82', eye: '#5C0E36' },
  amber: { disc: '#E3A21B', beak: '#C4860B', eye: '#5A3A02' },
  cyan: { disc: '#2099B5', beak: '#157D95', eye: '#06394A' },
  red: { disc: '#E4545C', beak: '#C73C45', eye: '#5E1217' },
  fuchsia: { disc: '#C24BD8', beak: '#A232BA', eye: '#4A0E57' },
} as const satisfies Record<string, CluColours>;

export type MarkColourKey = keyof typeof MARK_COLOURS_BY_KEY;

/** The keys in palette order. */
export const MARK_COLOUR_KEYS = Object.keys(
  MARK_COLOURS_BY_KEY
) as readonly MarkColourKey[];

/** The colours in palette order. */
export const MARK_COLOURS: readonly CluColours[] = MARK_COLOUR_KEYS.map(
  (key) => MARK_COLOURS_BY_KEY[key]
);

export function isMarkColourKey(value: unknown): value is MarkColourKey {
  return (
    typeof value === 'string' &&
    Object.prototype.hasOwnProperty.call(MARK_COLOURS_BY_KEY, value)
  );
}

/**
 * FNV-1a over the id, then murmur3's finalizer so the low bits mix too (FNV
 * alone leaves them nearly fixed, and every mark came out in two colours).
 * The colour of an agent the loaded list doesn't hold.
 */
export function markHashIndex(id: string): number {
  let hash = 0x811c9dc5;
  for (let i = 0; i < id.length; i += 1) {
    hash ^= id.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193);
  }
  hash ^= hash >>> 16;
  hash = Math.imul(hash, 0x85ebca6b);
  hash ^= hash >>> 13;
  hash = Math.imul(hash, 0xc2b2ae35);
  hash ^= hash >>> 16;
  return (hash >>> 0) % MARK_COLOURS.length;
}

/**
 * Each agent's place in the palette by creation order: oldest first, so every
 * new agent takes the next colour. Ties on the creation time fall back to the
 * id. Only for an agent without a stored colour (made before colours were
 * stored, until the backfill gives it one).
 */
export function markOrder(
  agents: readonly { id: string; createdAt: string }[]
): Map<string, number> {
  // A row without its creation time sorts first rather than breaking the sort.
  const sorted = [...agents].sort(
    (a, b) =>
      (a.createdAt ?? '').localeCompare(b.createdAt ?? '') ||
      a.id.localeCompare(b.id)
  );
  return new Map(
    sorted.map((agent, index) => [agent.id, index % MARK_COLOURS.length])
  );
}

export interface MarkedAgent {
  id: string;
  createdAt: string;
  /** The stored colour key; null or unknown falls back to creation order. */
  markColor?: string | null;
}

/**
 * Each agent's colour key: the one stored on it, else its place in creation
 * order. A stored colour never moves, so deleting an agent changes no other
 * agent's colour once every agent has one.
 */
export function markKeys(
  agents: readonly MarkedAgent[]
): Map<string, MarkColourKey> {
  const order = markOrder(agents);
  return new Map(
    agents.map((agent) => [
      agent.id,
      isMarkColourKey(agent.markColor)
        ? agent.markColor
        : MARK_COLOUR_KEYS[order.get(agent.id) ?? 0],
    ])
  );
}

/**
 * An agent's colours: its key (the stored one, else its place in the loaded
 * list's creation order), else its id's hash.
 */
export function markColours(
  id: string,
  keys: ReadonlyMap<string, MarkColourKey> | null
): CluColours {
  const key = keys?.get(id);
  return key ? MARK_COLOURS_BY_KEY[key] : MARK_COLOURS[markHashIndex(id)];
}
